import {
   ActualKeyPerformanceIndicator,
   ActualKeyPerformanceIndicatorPatch,
   ActualKeyPerformanceIndicatorPost,
   IntermediateReport,
   IntermediateReportStatus,
   InternalOrder,
   MediaPlan,
   MediaPlanFlight,
   UpdateIntermediateReport,
} from '@abovo/shared-models';
import { ROLE_SLUG_MEDIA_CONSULTANT } from '@abovo/shared-services';
import { ComplexTableColumn, SimpleDialogComponent, useSimpleDialog } from '@abovo/shared-ui-components';
import { isDefined } from '@abovo/shared-utils';
import {
   ActualKeyPerformanceIndicatorStore,
   DossierStore,
   FEATURE_NAME_V2,
   IntermediateCampaignReportActions,
   OrderMediaBundle,
   OrdersStore,
   TeamOwner,
   TeamStore,
   getCurrentMediaPlan,
   rowOfRole,
   useNgrx,
} from '@abovo/webvis-data-access';
import { ChangeDetectionStrategy, Component, Signal, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { startWith } from 'rxjs/operators';
import { REPORT_MODE_ENABLED } from '../../../../../../../constants';
import { toCardRow } from '../../../../../../../features/team/components/team-role-row.component';
import { ActualKeyPerformanceIndicatorInternal, IntermediateReportRow, KpiForm, KpiFormGroup } from '../../models/intermediate-report.models';
import { FormHelperService } from '../../services/form-helper-service';
import { ReportSaveMode, SaveLoadHelperService } from '../../services/save-load-helper.service';
import { RemarkDialogComponent, RemarkDialogData } from '../intermediate-report-detail/remark-dialog.component';
import { IntermediateReportGridComponent } from '../intermediate-report-grid/intermediate-report-grid.component';

@UntilDestroy()
@Component({
   selector: 'webvis-ui-intermediate-report-grid-container',
   templateUrl: 'intermediate-report-grid-container.component.html',
   changeDetection: ChangeDetectionStrategy.OnPush,
   imports: [IntermediateReportGridComponent],
   providers: [FormHelperService],
})
export class IntermediateReportGridContainerComponent {
   private actualKpiStore = inject(ActualKeyPerformanceIndicatorStore);
   reportModeActive = inject(REPORT_MODE_ENABLED);
   private saveLoadHelperService = inject(SaveLoadHelperService, { optional: true });
   private dossiersStore = inject(DossierStore);
   private ordersStore = inject(OrdersStore);
   private store = inject(Store);
   private translateService = inject(TranslateService);
   private formHelperService = inject(FormHelperService);
   private teamStore = inject(TeamStore);

   flights = input<Array<MediaPlanFlight>>();
   mediaPlan = input<MediaPlan | null>(null);
   reportForm = input.required<FormGroup<{ report_date: FormControl<Date> }>>();
   selectedView = input<'flights' | 'grouped' | 'totals'>('flights');
   intermediateReport = input<IntermediateReport>();
   readonly = input(false);
   hiddenFlightIds = input<null | number[]>(null);
   showMediaBundles = input(true);

   /** Allows hiding of the intermediate column for report mode */
   showIntermediateColumn = input(true);

   /**
    * Allows hiding of the planned value column for report mode. The KPI indicator column of the
    * planned group stays visible, since it also labels the intermediate/actual values and totals.
    */
   showPlannedColumn = input(true);

   private updateReport = useNgrx({
      featureKey: FEATURE_NAME_V2,
      dispatchAction: IntermediateCampaignReportActions.update,
      listenToActions: [IntermediateCampaignReportActions.updateSuccess],
   });
   private simpleDialog = useSimpleDialog({ invalidDisablesSave: true });
   private elaborationDialog?: { close: () => void; componentInstance: SimpleDialogComponent };

   // Only adjacent columns with the same memberOfGroup share a group header, so keep the groups together.
   private defaultColumns: Array<ComplexTableColumn> = [
      // product
      { type: 'text', field: 'specialist', header: 'grid.specialist', memberOfGroup: 'order.product' },
      { type: 'text', field: 'id', header: 'general.id', memberOfGroup: 'order.product' },
      { type: 'text', field: 'discipline', header: 'grid.discipline', memberOfGroup: 'order.product' },
      { type: 'text', field: 'product_combined', header: 'grid.product_combined', memberOfGroup: 'order.product' },
      { type: 'array', field: 'specifications', memberOfGroup: 'order.product', header: 'grid.specifications' },
      { type: 'date', field: 'placement_date', header: 'order.placement_date', memberOfGroup: 'order.product' },
      { type: 'date', field: 'end_date', header: 'order.end_date', memberOfGroup: 'order.product' },
      { type: 'currency', field: 'invoice_price.value', header: 'order.invoice_price', memberOfGroup: 'order.product' },
      // planned
      { type: 'text', field: 'kpi.name', repeatsPerSubRow: true, header: 'grid.kpi.kpi_indicator', memberOfGroup: 'intermediate_report.grid.planned' },
      {
         type: 'number',
         field: 'order_value',
         repeatsPerSubRow: true,
         header: 'grid.kpi.value',
         memberOfGroup: 'intermediate_report.grid.planned',
      },
      // intermediate
      {
         type: 'number',
         field: 'intermediate_value',
         repeatsPerSubRow: true,
         header: 'grid.kpi.value',
         memberOfGroup: 'intermediate_report.grid.intermediate',
      },
      // actuals
      { type: 'number', field: 'kpi_value', repeatsPerSubRow: true, header: 'grid.kpi.value', memberOfGroup: 'intermediate_report.actual' },
      { type: 'currency', field: 'rate', repeatsPerSubRow: true, header: 'intermediate_report.grid.rate', memberOfGroup: 'intermediate_report.actual' },
      {
         type: 'currency',
         field: 'kpi_investment',
         repeatsPerSubRow: true,
         header: 'intermediate_report.investment',
         memberOfGroup: 'intermediate_report.actual',
      },
      // order targeting information
      { type: 'text', field: 'targeting', memberOfGroup: 'intermediate_report.targeting', header: 'grid.targeting' },
      // menu
      { type: 'action', field: 'menu' },
   ];

   columns = computed(() => {
      return this.defaultColumns
         .filter((column) => {
            switch (column.field) {
               case 'specialist':
               case 'menu':
                  return !this.reportModeActive();
               case 'discipline':
                  return this.reportModeActive();
               case 'order_value':
                  return this.showPlannedColumn();
               case 'intermediate_value':
                  return this.showIntermediateColumn();
               case 'targeting':
                  return this.targetingDataFound();
               default:
                  return true;
            }
         })
         .map((column) => {
            if (column.field === 'kpi.name' && !this.showPlannedColumn()) {
               // Without the planned value column the KPI indicator no longer represents the 'planned' group
               return { ...column, memberOfGroup: null };
            }
            return column;
         });
   });

   private storeMediaPlan = this.store.selectSignal(getCurrentMediaPlan);
   private currentMediaPlan = computed(() => this.mediaPlan() ?? this.storeMediaPlan());
   private mediaPlanOwner = computed<TeamOwner>(() => ({ side: 'campaign', ownerId: this.currentMediaPlan()?.id ?? null }));

   /** The team is not on show here; it only says who the elaboration reaches. */
   private readTeam = this.teamStore.read(this.mediaPlanOwner);
   private storeOrders = this.ordersStore.ordersSplitPerFlight();
   orderKpiDataMap = signal(new Map<number, KpiForm>());
   reportDate = signal(new Date());

   /**
    * The store split already groups everything by flight and bundles the orders. We only keep the
    * flights that were passed in, drop internal products, and reuse the split's `mediaBundles`
    */
   private flightOrdersData = computed(() => {
      const storeOrders = this.storeOrders();
      const flights = this.flights();
      const isInternalProduct = (order: InternalOrder) => order.products?.[0]?.product_type?.slug === 'internal_product';
      if (!storeOrders?.size || !flights?.length) {
         return [];
      }
      return flights
         .map((flight) => ({
            id: flight.id,
            description: flight.name,
            bundles: (storeOrders.get(flight.id)?.mediaBundles ?? [])
               .map((bundle) => ({ ...bundle, orders: bundle.orders.filter((order) => !isInternalProduct(order)) }))
               .filter((bundle) => bundle.orders.length),
         }))
         .filter((section) => section.bundles.length);
   });

   /**
    * The rows shown in the grid, grouped into sections. In the 'flights' view there is one
    * section per flight; otherwise everything is collapsed into a single 'all flights' section.
    * Each section's rows are either the expanded underlying orders or, when {@link showMediaBundles}
    * is on, one aggregated row per bundle.
    */
   displayFlights: Signal<Array<{ id?: number; description: string; orders: Array<IntermediateReportRow> }>> = computed(() => {
      const flightOrdersData = this.flightOrdersData();
      const selectedView = this.selectedView();
      const showMediaBundles = this.showMediaBundles();
      return untracked(() => {
         if (!flightOrdersData.length) {
            return [];
         }
         if (selectedView === 'flights') {
            return flightOrdersData.map((orderData) => ({
               id: orderData.id,
               description: orderData.description,
               orders: this.toGridRows(orderData.bundles, showMediaBundles),
            }));
         }
         const allBundles = flightOrdersData.flatMap((orderData) => orderData.bundles);
         return [{ description: this.translateService.instant('intermediate_report.all_flights'), orders: this.toGridRows(allBundles, showMediaBundles) }];
      });
   });

   private targetingDataFound = signal(false);

   private targetingDataFoundEffect = effect(() => {
      // We want to hide the targeting column when there are no orders with anything to display in it
      const orders = this.flightOrdersData().flatMap((section) => section.bundles.flatMap((bundle) => bundle.orders));
      untracked(() => {
         if (this.targetingDataFound()) {
            return;
         }
         this.targetingDataFound.set(orders.some((order) => !!order.targeting));
      });
   });

   private reportDateEffect = effect(() => {
      // We need to watch for changes on the report date form and pass any new date to the grids
      const reportDateControl = this.reportForm()?.controls.report_date;
      if (!reportDateControl) {
         return;
      }
      untracked(() => {
         reportDateControl.valueChanges.pipe(untilDestroyed(this), startWith(reportDateControl.value)).subscribe({
            next: (value) => {
               this.reportDate.set(value);
               this.updateUnsavedChanges();
            },
         });
      });
   });

   private loadFlightOrdersEffect = effect(() => {
      const flightIds = this.flights()?.map((flight) => flight.id);
      untracked(() => {
         if (flightIds) {
            this.ordersStore.loadFlightOrders({ campaign_flight_ids: flightIds });
         }
      });
   });
   // On the id, not the report: a report update (a saved remark) would reload the actual KPIs and rebuild the grid forms, losing typed values
   private readonly reportId = computed(() => this.intermediateReport()?.id);
   private readonly reportApproved = computed(() => this.intermediateReport()?.status?.value === 'approved');
   private intermediateReportEffect = effect(() => {
      const reportId = this.reportId();
      if (reportId) {
         untracked(() => this.actualKpiStore.get({ include: 'kpi,order', intermediate_report_ids: [reportId] }));
      }
   });
   private reportApprovedEffect = effect(() => {
      const approved = this.reportApproved();
      untracked(() => this.formHelperService.disableEdit.set(approved));
   });
   private saveButtonEffect = effect(() => {
      const saveEvent = this.saveLoadHelperService?.saveEvent();
      if (saveEvent) {
         // actual user button click
         untracked(() => this.createUpdateDelete(saveEvent.mode));
      }
   });
   private dataLoadedEffect = effect(() => {
      const loaded = this.actualKpiStore.loaded();
      const actualKpis = this.actualKpiStore.entities();
      const flightOrdersData = this.flightOrdersData();
      const _showMediaBundles = this.showMediaBundles();
      if (loaded && flightOrdersData.length) {
         untracked(() => this.rebuildKpiDataMap(actualKpis));
      }
   });
   private kpiStoreSavedEffect = effect(() => {
      if (this.actualKpiStore.saved()) {
         untracked(() => {
            this.resetDirtyAndDelete();
            this.updateUnsavedChanges();
         });
      }
   });
   private dossierStoreEffect = effect(() => {
      const loading = this.dossiersStore.loading();
      const loaded = this.dossiersStore.loaded();
      untracked(() => {
         if (!loading && loaded) {
            this.elaborationDialog?.close();
         }
         if (!loading && !loaded) {
            this.elaborationDialog?.componentInstance.saving.set(false);
         }
      });
   });

   /**
    * Resolves a section's bundles into grid rows: the expanded underlying orders when the bundle
    * view is off, or one aggregated row per bundle when it is on.
    */
   private toGridRows(bundles: Array<OrderMediaBundle>, showMediaBundles: boolean): Array<IntermediateReportRow> {
      if (!showMediaBundles) {
         return bundles.flatMap((bundle) => bundle.orders);
      }
      return bundles.map((bundle) => this.toBundleRow(bundle));
   }

   /**
    * Represents an aggregated bundle as a single grid row. A bundle already mirrors every order
    * field this grid reads, only its id lives in `orderGroupingKey`.
    */
   private toBundleRow(bundle: OrderMediaBundle): IntermediateReportRow {
      return { ...bundle, id: bundle.orderGroupingKey };
   }

   /**
    * Sums the actual KPI values per KPI slug, so a multi-order bundle row shows one entry per KPI
    * instead of one per underlying order. For a single-order row this is the identity.
    */
   private aggregateActualKpisPerSlug(actualKpis: Array<ActualKeyPerformanceIndicatorInternal>): Array<ActualKeyPerformanceIndicatorInternal> {
      const perSlug = new Map<string, ActualKeyPerformanceIndicatorInternal>();
      actualKpis.forEach((actualKpi) => {
         const existing = perSlug.get(actualKpi.kpi.slug);
         if (existing) {
            existing.kpi_value = isDefined(actualKpi.kpi_value) ? (existing.kpi_value ?? 0) + actualKpi.kpi_value : existing.kpi_value;
            existing.kpi_investment = isDefined(actualKpi.kpi_investment) ? (existing.kpi_investment ?? 0) + actualKpi.kpi_investment : existing.kpi_investment;
         } else {
            perSlug.set(actualKpi.kpi.slug, { ...actualKpi });
         }
      });
      return Array.from(perSlug.values());
   }

   /**
    * Initialise the order kpi data map that holds (almost) all data for the component, keyed by
    * the rendered row id (an order id, or a bundle id in the media-bundle view).
    */
   private rebuildKpiDataMap(storeActualKpis: Array<ActualKeyPerformanceIndicator>): void {
      const actualKpis = storeActualKpis.flatMap((actualKpi) => (actualKpi.kpi ? [{ ...actualKpi, kpi: actualKpi.kpi }] : []));
      const dataMap = new Map<number, KpiForm>();
      const showBundles = this.showMediaBundles();
      this.flightOrdersData()
         .flatMap((section) => section.bundles)
         .forEach((bundle) => {
            if (showBundles) {
               // One aggregated row per bundle, keyed by the bundle id, summing its orders' actuals per KPI.
               const bundleOrderIds = bundle.orders.map((order) => order.id);
               const bundleActualKpis = this.aggregateActualKpisPerSlug(
                  actualKpis.filter((actualKpi) => !!actualKpi.order && bundleOrderIds.includes(actualKpi.order.id)),
               );
               dataMap.set(bundle.orderGroupingKey, this.formHelperService.buildKpiForm(bundleActualKpis, bundle.orders, this.toBundleRow(bundle)));
            } else {
               // One row per underlying order, keyed by the order id.
               bundle.orders.forEach((order) => {
                  const orderActualKpis = actualKpis.filter((actualKpi) => actualKpi.order?.id === order.id);
                  dataMap.set(order.id, this.formHelperService.buildKpiForm(orderActualKpis, [order], order));
               });
            }
         });
      this.orderKpiDataMap.set(dataMap);
   }

   /**
    * After saving we have to reset the list of ids to be deleted and mark all forms as pristine
    */
   private resetDirtyAndDelete() {
      if (this.saveLoadHelperService) {
         this.saveLoadHelperService.deleteIdsList = new Set<number>();
      }
      this.orderKpiDataMap().forEach((kpiForm) => {
         kpiForm.formGroup.controls.forEach((control) => control.markAsPristine());
      });
   }

   private createUpdateDelete(mode: ReportSaveMode) {
      const createUpdateData = this.collectChanges();
      if (createUpdateData.length && mode === 'elaboration') {
         this.openReportElaborationDialog(createUpdateData);
      } else {
         this.saveReportChanges(createUpdateData);
      }
   }

   private collectChanges() {
      return Array.from(this.orderKpiDataMap().values())
         .flatMap((kpiRatesAndForm) => {
            return kpiRatesAndForm.formGroup.controls.flatMap((control) => {
               if (!control.dirty) {
                  // nothing to do for this formarray
                  return [];
               }
               return this.buildSaveDataForRow(kpiRatesAndForm.orders, control);
            });
         })
         .filter(Boolean);
   }

   /** The static report page has no save helper, so there is nothing unsaved there */
   protected updateUnsavedChanges(): void {
      this.saveLoadHelperService?.updateUnsavedChanges(this.reportForm(), this.orderKpiDataMap());
   }

   private reportStatus(): IntermediateReportStatus {
      const isDone = Array.from(this.orderKpiDataMap().values()).map((kpiRatesAndForm) => this.formHelperService.isReportStatusDone(kpiRatesAndForm.formGroup));
      return isDone.every((done) => done) ? 'done' : 'in_progress';
   }

   private delete() {
      const deleteIds = Array.from(this.saveLoadHelperService?.deleteIdsList ?? []);
      if (deleteIds.length) {
         this.actualKpiStore.deleteMulti(deleteIds);
      }
   }

   /**
    * Turns one dirty KPI form row into the actual-KPI records to save. A normal row has a single order
    * and yields a single record; a bundle row holds several orders, so the entered value and investment
    * are split across them proportionally to each order's planned value for this KPI (see
    * {@link FormHelperService.distributeProportionally}). Whether each order becomes a create or an
    * update is decided per `(order, slug)` against the already-loaded actual KPIs, since the row's own
    * form `id` only reflects one order in the bundle case.
    */
   private buildSaveDataForRow(orders: Array<InternalOrder>, formControl: KpiFormGroup) {
      const reportId = this.intermediateReport()?.id;
      if (!reportId) {
         return [];
      }
      const formData = formControl.getRawValue();
      const slug = formData.kpi.slug;
      const weights = orders.map((order) => this.formHelperService.plannedValueForSlug(order, slug));
      const kpiValues = this.formHelperService.distributeProportionally(formData.kpi_value, weights);
      const kpiInvestments = this.formHelperService.distributeProportionally(formData.kpi_investment, weights);
      const existingKpis = this.actualKpiStore.entities();
      const anyOrderHasKpi = orders.some((order) => this.formHelperService.orderHasKpi(order, slug));
      return orders
         .map((order, index) => {
            const existing = existingKpis.find((entity) => entity.order?.id === order.id && entity.kpi?.slug === slug);
            if (!existing && anyOrderHasKpi && !this.formHelperService.orderHasKpi(order, slug)) {
               // In a bundle this KPI belongs to specific orders only; don't create a stray record on an
               // order that never had it. A freshly added KPI (no order has it yet) falls through and is
               // saved onto every order in the row, even when no value was entered yet.
               return null;
            }
            return {
               ...(existing && { id: existing.id }),
               order: order.id,
               report: reportId,
               kpi: formData.kpi.id,
               kpi_value: kpiValues[index],
               kpi_investment: kpiInvestments[index],
            };
         })
         .filter(isDefined);
   }

   /** Asks for the elaboration and shows who reads it: the media consultant, whom the endpoint addresses itself. */
   private openReportElaborationDialog(createUpdateData: Array<ActualKeyPerformanceIndicatorPost | ActualKeyPerformanceIndicatorPatch>): void {
      const consultantRow = rowOfRole(this.teamStore.rowsOf(this.mediaPlanOwner()), ROLE_SLUG_MEDIA_CONSULTANT);
      this.elaborationDialog = this.simpleDialog.openDialog(
         {
            title: 'intermediate_report.report_specialist_elaboration',
            confirmButton: 'general.send',
            dialogData: {
               recipient: consultantRow ? toCardRow(consultantRow) : null,
               required: true,
               labels: {
                  remark: 'general.additional_remark',
                  remark_placeholder: 'intermediate_report.additional_remark_placeholder',
               },
            } as RemarkDialogData,
            actions: {
               accept: ({ remark }: { remark: string }) => {
                  const reportId = this.intermediateReport()?.id;
                  if (!reportId) {
                     return;
                  }
                  this.dossiersStore.addDossier({
                     dossier: {
                        specialistElaboration: remark,
                        intermediateReportId: reportId,

                        // Used to save correctly in the store
                        id: reportId,
                        type: 'reports',
                     },
                  });
                  this.saveReportChanges(createUpdateData);
               },
            },
         },
         RemarkDialogComponent,
      );
   }

   private saveReportChanges(createUpdateData: Array<ActualKeyPerformanceIndicatorPost | ActualKeyPerformanceIndicatorPatch>): void {
      this.saveReportData();
      this.delete();
      if (createUpdateData.length) {
         this.actualKpiStore.createUpdateMulti(createUpdateData);
      }
   }

   /** Saves the report date and the status in one request, so a second response can never overwrite the first */
   private saveReportData(): void {
      const intermediateReport = this.intermediateReport();
      if (!intermediateReport) {
         return;
      }
      const reportForm = this.reportForm();
      const reportStatus = this.reportStatus();
      const report: UpdateIntermediateReport = {
         id: intermediateReport.id,
         ...(reportForm.dirty && { report_date: reportForm.controls.report_date.value }),
         ...(reportStatus !== intermediateReport.status?.value && { status: reportStatus }),
      };
      if (Object.keys(report).length === 1) {
         // Only the id, nothing to save
         return;
      }
      this.updateReport.dispatch({ report }).onSuccess.then(() => {
         reportForm.markAsPristine();
         this.updateUnsavedChanges();
      });
   }
}
